/**
 * File: PeerReviewChatPanel.tsx
 * Description: Full-width conversational panel shown when a checklist row is
 *   expanded. Renders Archie's recommendation, the Optimistic vs Pessimistic
 *   debate as a chat thread (with inline feedback), and an "Ask Archie" input
 *   so the architect can join the discussion.
 * Author: GitHub Copilot
 */
import {
  ArrowPathIcon,
  BookmarkSquareIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  UserIcon,
} from '@heroicons/react/24/solid';
import React, { useState } from 'react';

import {
  PeerReviewCitation,
  PeerReviewDebateMessage,
  PeerReviewDebateRole,
  PeerReviewRecommendation,
} from '../../types/review';

import { PeerReviewFeedbackControl } from './PeerReviewFeedbackControl';
import { PeerReviewRecommendationBox } from './PeerReviewRecommendationBox';

const OPTIMISTIC_AGENT = 'OptimisticArchieAgent';
const PESSIMISTIC_AGENT = 'PessimisticArchieAgent';
const ASK_PLACEHOLDER = 'Ask Archie a follow-up question…';
const ASK_BUTTON_LABEL = 'Send';
const REDEBATE_BUTTON_LABEL = 'Let the agents debate again';
// Sent verbatim as the reviewer's chat message: phrased so it reads cleanly as
// a bubble while directing both debaters to re-read the applied changes and
// comments and re-assess the verdict for this item.
const REDEBATE_PROMPT =
  'Let the agents debate again — re-read the updated document and all ' +
  'comments, then reconsider whether this item is now genuinely resolved.';
const THINKING_MESSAGE = 'Archie is thinking…';
const EMPTY_THREAD_MESSAGE =
  'No debate yet. Run Archie to see the Optimistic vs Pessimistic discussion.';
const CITATIONS_LABEL = 'Source cite';
const VIEW_CITATIONS_LABEL = 'view';

interface CitationGroup {
  documentTitle: string;
  rules: string[];
}

const getCitationGroups = (citations: PeerReviewCitation[]): CitationGroup[] => {
  const groupedCitations = new Map<string, Set<string>>();

  citations.forEach((citation) => {
    const existingRules = groupedCitations.get(citation.document_title) ?? new Set<string>();
    existingRules.add(citation.principle_or_rule);
    groupedCitations.set(citation.document_title, existingRules);
  });

  return Array.from(groupedCitations.entries()).map(([documentTitle, rules]) => ({
    documentTitle,
    rules: Array.from(rules),
  }));
};

interface ChatRoleMeta {
  name: string;
  bubble: string;
  avatar: string;
  labelColor: string;
  align: 'left' | 'right';
  Icon: React.ComponentType<{ className?: string }>;
}

const CHAT_ROLE_META: Record<PeerReviewDebateRole, ChatRoleMeta> = {
  optimistic: {
    name: 'Optimistic Archie',
    bubble: 'bg-green-50 text-green-900 border-green-200',
    avatar: 'bg-green-600',
    labelColor: 'text-green-700',
    align: 'left',
    Icon: SparklesIcon,
  },
  pessimistic: {
    name: 'Pessimistic Archie',
    bubble: 'bg-red-50 text-red-900 border-red-200',
    avatar: 'bg-red-600',
    labelColor: 'text-red-700',
    align: 'left',
    Icon: SparklesIcon,
  },
  user: {
    name: 'You',
    bubble: 'bg-indigo-600 text-white border-indigo-600',
    avatar: 'bg-indigo-600',
    labelColor: 'text-indigo-200',
    align: 'right',
    Icon: UserIcon,
  },
};

const AGENT_BY_ROLE: Partial<Record<PeerReviewDebateRole, string>> = {
  optimistic: OPTIMISTIC_AGENT,
  pessimistic: PESSIMISTIC_AGENT,
};

/**
 * Compact citation chips rendered under a debate bubble.
 */
const MessageCitations: React.FC<{ citations: PeerReviewCitation[] }> = ({
  citations,
}) => {
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const citationGroups = getCitationGroups(citations);

  if (citationGroups.length === 0) {
    return null;
  }

  const toggleSource = (source: string) => {
    setExpandedSources((previousSources) => {
      const nextSources = new Set(previousSources);
      if (nextSources.has(source)) {
        nextSources.delete(source);
      } else {
        nextSources.add(source);
      }
      return nextSources;
    });
  };

  return (
    <div className="mt-1.5 flex flex-col gap-1">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {CITATIONS_LABEL}
      </p>
      {citationGroups.map((citationGroup, index) => {
        const isExpanded = expandedSources.has(citationGroup.documentTitle);

        return (
          <div
            key={`${citationGroup.documentTitle}-${index}`}
            className="rounded-md border border-slate-200 bg-white/80 px-2 py-1 text-[11px] text-slate-600"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-1">
                <BookmarkSquareIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
                <div>
                  <p className="font-semibold text-slate-700">
                    {citationGroup.documentTitle}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => toggleSource(citationGroup.documentTitle)}
                className="text-[11px] font-medium text-[#336094] underline underline-offset-2 hover:text-[#6e94c0]"
              >
                {VIEW_CITATIONS_LABEL}
              </button>
            </div>
            {isExpanded && (
              <ul className="mt-1 list-disc space-y-1 pl-6 leading-snug text-slate-600">
                {citationGroup.rules.map((rule) => (
                  <li key={`${citationGroup.documentTitle}-${rule}`}>{rule}</li>
                ))}
              </ul>
            )}
          </div>
        );})}
    </div>
  );
};

/**
 * Circular avatar for a chat participant.
 */
const ChatAvatar: React.FC<{ meta: ChatRoleMeta }> = ({ meta }) => {
  const { Icon } = meta;
  return (
    <span
      className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-white ${meta.avatar}`}
    >
      <Icon className="h-4 w-4" />
    </span>
  );
};

interface ChatMessageProps {
  message: PeerReviewDebateMessage;
  projectId: number;
  checklistItemId: number;
}

/**
 * A single chat message with avatar, bubble and (for agents) feedback.
 */
const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  projectId,
  checklistItemId,
}) => {
  const meta = CHAT_ROLE_META[message.role];
  const isRight = meta.align === 'right';
  const agent = AGENT_BY_ROLE[message.role];
  const rowClass = `flex items-end gap-2 ${
    isRight ? 'flex-row-reverse' : 'flex-row'
  }`;
  const author = message.role === 'user' && message.author
    ? message.author
    : meta.name;

  return (
    <div className={rowClass}>
      <ChatAvatar meta={meta} />
      <div className={`max-w-[78%] ${isRight ? 'items-end' : 'items-start'}`}>
        <div
          className={`mb-0.5 text-[11px] font-semibold ${
            isRight ? 'text-right text-indigo-700' : meta.labelColor
          }`}
        >
          {author}
        </div>
        <div
          className={`rounded-2xl border px-3 py-2 text-xs leading-snug shadow-sm ${meta.bubble}`}
        >
          {message.message}
        </div>
        <MessageCitations citations={message.citations ?? []} />
        {agent && (
          <PeerReviewFeedbackControl
            projectId={projectId}
            agent={agent}
            context={{
              checklist_item_id: checklistItemId,
              role: message.role,
              message: message.message,
              citations: message.citations,
            }}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Animated "typing" indicator shown while Archie is responding.
 */
const ThinkingIndicator: React.FC = () => (
  <div className="flex items-center gap-2 text-xs text-gray-500">
    <span className="flex gap-1">
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400" />
    </span>
    {THINKING_MESSAGE}
  </div>
);

interface PeerReviewChatPanelProps {
  checklistItemId: number;
  projectId: number;
  messages: PeerReviewDebateMessage[];
  recommendation?: PeerReviewRecommendation | null;
  recommendationLoading: boolean;
  asking: boolean;
  canAsk: boolean;
  onAsk: (question: string) => void;
}

/**
 * Expanded chat experience for a single checklist item.
 */
export const PeerReviewChatPanel: React.FC<PeerReviewChatPanelProps> = ({
  checklistItemId,
  projectId,
  messages,
  recommendation,
  recommendationLoading,
  asking,
  canAsk,
  onAsk,
}) => {
  const [question, setQuestion] = useState('');

  const handleSend = () => {
    const trimmed = question.trim();
    if (!trimmed || asking) {
      return;
    }
    onAsk(trimmed);
    setQuestion('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleRedebate = () => {
    if (asking) {
      return;
    }
    onAsk(REDEBATE_PROMPT);
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-gray-50/70 p-3">
      <PeerReviewRecommendationBox
        recommendation={recommendation}
        isLoading={recommendationLoading}
      />

      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
        {messages.length === 0 && (
          <p className="text-xs text-gray-500">{EMPTY_THREAD_MESSAGE}</p>
        )}
        {messages.map((message, index) => (
          <ChatMessage
            key={`${message.role}-${index}`}
            message={message}
            projectId={projectId}
            checklistItemId={checklistItemId}
          />
        ))}
        {asking && <ThinkingIndicator />}

        {canAsk && (
          <div className="mt-1 flex flex-col gap-2 border-t border-gray-100 pt-3">
            <button
              type="button"
              onClick={handleRedebate}
              disabled={asking}
              className="inline-flex w-fit items-center gap-1.5 self-start rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-[11px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowPathIcon className="h-3.5 w-3.5" />
              {REDEBATE_BUTTON_LABEL}
            </button>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={handleKeyDown}
                disabled={asking}
                placeholder={ASK_PLACEHOLDER}
                aria-label="Ask Archie a follow-up question"
                className="flex-1 rounded-full border border-gray-300 bg-white px-4 py-2 text-xs text-gray-700 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-300 disabled:opacity-60"
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={asking || question.trim().length === 0}
                aria-label={ASK_BUTTON_LABEL}
                className="flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {ASK_BUTTON_LABEL}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
